import React, { useState, useEffect, useRef } from 'react';
import { getMetaTemplatesAll, createMetaTemplate, uploadWhatsAppMedia, saveTemplateDraft, updateTemplateDraft, getTemplateDrafts, getTemplateDraft, submitTemplateDraft, deleteTemplateDraft } from '../utils/api';
import { FaFileAlt, FaCheckCircle, FaClock, FaTimesCircle, FaPlus, FaEye, FaCopy, FaSave, FaTrashAlt, FaPaperPlane } from 'react-icons/fa';
import { useToast } from './Toast';
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview';
import './WhatsApp.css';

const STARTER_TEMPLATES = [
  // 2 marketing / promotion
  { id: 'marketing_new_listings', kind: 'MARKETING', nameAr: 'تسويق - عروض جديدة', nameEn: 'Marketing - New listings', name: 'new_listings_promo', category: 'MARKETING', body_text: 'مرحباً {{1}}، تتوفر الآن عروض عقارية جديدة في {{2}}. للصور والتفاصيل يرجى الرد على هذه الرسالة.' },
  { id: 'marketing_price_drop', kind: 'MARKETING', nameAr: 'تسويق - تخفيض سعر', nameEn: 'Marketing - Price drop', name: 'price_drop_alert', category: 'MARKETING', body_text: 'مرحباً {{1}}، تم تخفيض سعر أحد العروض المناسبة في {{2}}. هل ترغب بإرسال التفاصيل الآن؟' },
  // 2 utility / service
  { id: 'utility_viewing_confirmation', kind: 'UTILITY', nameAr: 'خدمة - تأكيد المعاينة', nameEn: 'Service - Viewing confirmation', name: 'viewing_confirmation', category: 'UTILITY', body_text: 'مرحباً {{1}}، نؤكد موعد معاينة العقار في {{2}}. يرجى الرد لتأكيد الحضور.' },
  { id: 'utility_documents_reminder', kind: 'UTILITY', nameAr: 'خدمة - تذكير المستندات', nameEn: 'Service - Documents reminder', name: 'documents_reminder', category: 'UTILITY', body_text: 'مرحباً {{1}}، نود تذكيركم بالمستندات المطلوبة لإكمال الخدمة. إذا رغبت، نرسل لكم القائمة الآن.' },
  // 1 first-contact / welcome for new leads (common in physical offices)
  { id: 'first_contact_welcome', kind: 'FIRST_CONTACT', nameAr: 'الرسالة الأولى - ترحيب عميل جديد', nameEn: 'First contact - New lead welcome', name: 'new_lead_welcome', category: 'UTILITY', body_text: 'نشكركم على تواصلكم مع {{1}} للعقارات وإدارة الأملاك، ويسعدنا خدمتكم. يمكننا الآن إرسال العروض المناسبة حسب احتياجكم.' }
];

const BODY_CHAR_LIMIT = 1024;
const DRAFT_KEY = 'real_wa_template_draft';
const EMPTY_CREATE_FORM = {
  name: '',
  category: 'UTILITY',
  body_text: '',
  layout: 'standard',
  carousel_cards: [
    { media_url: '', url_button_example: '' },
    { media_url: '', url_button_example: '' },
  ],
  carousel_quick_reply_text: '',
  carousel_url_button_text: '',
  carousel_url: '',
  header_format: '',
  header_text: '',
  header_media_url: '',
  footer_text: '',
  button_type: '',
  button_url_text: '',
  button_url: '',
  button_phone_text: '',
  button_phone_number: '',
  button_secondary_url_text: '',
  button_secondary_url: '',
  button_secondary_phone_text: '',
  button_secondary_phone_number: '',
  button_quick_1: '',
  button_quick_2: '',
  button_quick_3: ''
};

// Body snippets for one-click insert: text = inserted (includes {{1}} etc), short = button label (user-friendly, no placeholders)
const BODY_SNIPPETS = [
  { textAr: 'مرحباً {{1}}، ', textEn: 'Hello {{1}}, ', shortAr: 'مرحباً + اسم', shortEn: 'Hello + name' },
  { textAr: 'لدينا عقار يناسبك. ', textEn: 'We have a property that suits you. ', shortAr: 'عقار يناسبك', shortEn: 'Property suits' },
  { textAr: 'للتفاصيل اضغط على الرابط. ', textEn: 'Click the link for details. ', shortAr: 'للتفاصيل', shortEn: 'For details' },
  { textAr: 'نذكرك بمعاينة العقار. ', textEn: 'Reminder: property viewing. ', shortAr: 'تذكير معاينة', shortEn: 'Viewing reminder' },
  { textAr: 'شكراً لاهتمامك. ', textEn: 'Thanks for your interest. ', shortAr: 'شكراً', shortEn: 'Thanks' },
  { textAr: 'هل ما زلت مهتماً؟ ', textEn: 'Still interested? ', shortAr: 'مهتماً؟', shortEn: 'Interested?' }
];

// Variable options for dropdown: value = placeholder, label = friendly name
const VARIABLE_OPTIONS = [
  { value: '{{1}}', labelAr: 'اسم العميل', labelEn: 'Client name' },
  { value: '{{2}}', labelAr: 'المنطقة', labelEn: 'Area' },
  { value: '{{3}}', labelAr: 'نوع العقار', labelEn: 'Property type' }
];

const TEMPLATE_NAME_WORD_MAP = {
  'تذكير': 'reminder',
  'موعد': 'appointment',
  'متابعة': 'followup',
  'عرض': 'offer',
  'عقار': 'property',
  'عقارات': 'properties',
  'زيارة': 'visit',
  'تأكيد': 'confirm',
  'ترحيب': 'welcome',
  'عميل': 'client',
  'عميله': 'client',
  'عميلة': 'client',
  'استفسار': 'inquiry',
  'تفاصيل': 'details',
  'جدولة': 'schedule',
  'حجز': 'booking',
  'تجديد': 'renewal'
};

const TEMPLATE_NAME_CHAR_MAP = {
  'ا': 'a', 'أ': 'a', 'إ': 'a', 'آ': 'a',
  'ب': 'b', 'ت': 't', 'ث': 'th', 'ج': 'j', 'ح': 'h', 'خ': 'kh',
  'د': 'd', 'ذ': 'dh', 'ر': 'r', 'ز': 'z', 'س': 's', 'ش': 'sh',
  'ص': 's', 'ض': 'd', 'ط': 't', 'ظ': 'z', 'ع': 'a', 'غ': 'gh',
  'ف': 'f', 'ق': 'q', 'ك': 'k', 'ل': 'l', 'م': 'm', 'ن': 'n',
  'ه': 'h', 'ة': 'h', 'و': 'w', 'ؤ': 'w', 'ي': 'y', 'ى': 'a', 'ئ': 'y',
  'ء': '', 'ـ': ''
};

const normalizeTemplateNamePreview = (rawName) => {
  const input = String(rawName || '').trim().toLowerCase();
  if (!input) return '';
  const tokens = input.split(/[\s\-_]+/).filter(Boolean);
  const mapped = tokens.map((token) => {
    if (/^[a-z0-9]+$/.test(token)) return token;
    if (TEMPLATE_NAME_WORD_MAP[token]) return TEMPLATE_NAME_WORD_MAP[token];
    let translit = '';
    for (const ch of token) {
      if (TEMPLATE_NAME_CHAR_MAP[ch] !== undefined) translit += TEMPLATE_NAME_CHAR_MAP[ch];
      else if (/[a-z0-9]/i.test(ch)) translit += ch.toLowerCase();
    }
    return translit.replace(/[^a-z0-9]/g, '');
  }).filter(Boolean);
  let clean = mapped.join('_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  if (!clean) clean = 'template_ar';
  if (!/^[a-z]/.test(clean)) clean = `tpl_${clean}`;
  return clean.slice(0, 100);
};

const detectDirection = (value = '') => (/[\u0600-\u06FF]/.test(String(value || '')) ? 'rtl' : 'ltr');

// Suggest template name from body text (Arabic/English keywords + fallback)
const suggestTemplateNameFromBody = (body) => {
  if (!body || typeof body !== 'string') return null;
  const t = body.replace(/\{\{\d+\}\}/g, '').trim();
  if (t.length < 4) return null;
  const lower = t.toLowerCase();
  const words = t.split(/\s+/).map((w) => w.replace(/[^\w\u0600-\u06FF]/g, ''));
  const has = (...keys) => keys.some((k) => lower.includes(k) || words.some((w) => w.includes(k)));
  if (has('عرض', 'offer', 'عروض')) return 'property_offer';
  if (has('تذكير', 'reminder')) return 'reminder';
  if (has('معاينة', 'viewing', 'معاينه')) return 'viewing_reminder';
  if (has('ترحيب', 'greeting', 'مرحبا', 'مرحباً', 'hello')) return 'client_greeting';
  if (has('طلب', 'request', 'معلومات', 'info')) return 'request_info';
  if (has('متابعة', 'follow', 'followup')) return 'follow_up';
  if (has('عقار', 'property', 'عقارات')) return 'property_message';
  if (has('تواصل', 'contact', 'اتصال')) return 'contact_request';
  // Fallback: map common Arabic words to English, build name from first meaningful words
  const arToEn = { عرض: 'offer', عقار: 'property', تذكير: 'reminder', معاينة: 'viewing', مرحباً: 'hi', ترحيب: 'greeting', طلب: 'request', معلومات: 'info', متابعة: 'followup', تفاصيل: 'details', شكراً: 'thanks', عميل: 'client', جديد: 'new', خاص: 'special' };
  const out = [];
  for (const w of words.slice(0, 4)) {
    const clean = w.replace(/[^\u0600-\u06FFa-zA-Z0-9]/g, '');
    if (!clean) continue;
    const eng = arToEn[clean] || (clean.match(/^[a-z0-9]+$/) ? clean : null);
    if (eng) out.push(eng);
  }
  const name = out.filter(Boolean).join('_').replace(/[^a-z0-9_]/g, '') || 'message';
  return name.length >= 3 ? name : name + '_template';
};

/** Preview props for WhatsAppTemplatePreview from Meta template.components (CAROUSEL) or draft carousel_json. */
function extractCarouselPreviewFromTemplate(t) {
  if (!t) return null;
  if (t.carousel_json) {
    try {
      const cj = JSON.parse(t.carousel_json);
      if (Array.isArray(cj.carousel_cards) && cj.carousel_cards.length >= 2) {
        return {
          layout: 'carousel',
          carouselCards: cj.carousel_cards.map((c) => ({
            media_url: String(c?.media_url || ''),
            url_button_example: String(c?.url_button_example || ''),
          })),
          carouselQuickReplyText: String(cj.carousel_quick_reply_text || ''),
          carouselUrlButtonText: String(cj.carousel_url_button_text || ''),
          carouselUrl: String(cj.carousel_url || ''),
        };
      }
    } catch (_) { /* ignore */ }
  }
  const comps = t.components || t.meta_components || [];
  const car = comps.find((c) => String(c.type || '').toUpperCase() === 'CAROUSEL');
  if (!car || !Array.isArray(car.cards) || car.cards.length < 1) return null;
  const normBtnType = (b) => String(b?.type || '').toLowerCase();
  const carouselCards = [];
  let carouselQuickReplyText = '';
  let carouselUrlButtonText = '';
  let carouselUrl = '';
  for (let i = 0; i < car.cards.length; i++) {
    const card = car.cards[i];
    const cardComps = card.components || [];
    const header = cardComps.find((c) => String(c.type || '').toUpperCase() === 'HEADER');
    const handle = header?.example?.header_handle?.[0] || '';
    const btnComp = cardComps.find((c) => String(c.type || '').toUpperCase() === 'BUTTONS');
    const buttons = btnComp?.buttons || [];
    const qr = buttons.find((b) => normBtnType(b) === 'quick_reply');
    const urlB = buttons.find((b) => normBtnType(b) === 'url');
    if (i === 0) {
      if (qr?.text) carouselQuickReplyText = String(qr.text);
      if (urlB?.text) carouselUrlButtonText = String(urlB.text);
      if (urlB?.url) carouselUrl = String(urlB.url);
    }
    const slugEx = Array.isArray(urlB?.example) && urlB.example[0] != null ? String(urlB.example[0]) : '';
    carouselCards.push({
      media_url: handle ? String(handle) : '',
      url_button_example: slugEx,
    });
  }
  return {
    layout: 'carousel',
    carouselCards,
    carouselQuickReplyText,
    carouselUrlButtonText,
    carouselUrl,
  };
}

const WhatsAppTemplates = ({ language = 'ar' }) => {
  const toast = useToast();
  const validateTemplateName = (name) => {
    if (!name || !name.trim()) return null;
    const n = name.trim();
    if (n.length < 3) return language === 'ar' ? 'الاسم قصير جداً (3 أحرف على الأقل)' : 'Name too short (min 3 chars)';
    if (n.length > 512) return language === 'ar' ? 'الاسم طويل جداً' : 'Name too long';
    return null;
  };
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [metaTokenError, setMetaTokenError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all | APPROVED | PENDING | REJECTED
  const [filterCategory, setFilterCategory] = useState('all'); // all | UTILITY | MARKETING | AUTHENTICATION
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name | status | category
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState(''); // Instant local preview before upload
  const [editingFromTemplate, setEditingFromTemplate] = useState(null); // { name } when editing from existing
  const [selectedStarterTemplateId, setSelectedStarterTemplateId] = useState('');
  const [selectedApprovedCopyName, setSelectedApprovedCopyName] = useState('');
  const [variableSamples, setVariableSamples] = useState({ 1: 'أحمد', 2: 'الرياض', 3: 'شقة' });
  const headerMediaRef = useRef(''); // Persist blob URL across step navigation so image doesn't disappear
  const [drafts, setDrafts] = useState([]);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);

  useEffect(() => {
    const url = headerPreviewUrl || createForm.header_media_url;
    if (url) headerMediaRef.current = url;
    else if (!headerPreviewUrl && !createForm.header_media_url) headerMediaRef.current = '';
  }, [headerPreviewUrl, createForm.header_media_url]);

  useEffect(() => {
    loadTemplates();
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    try {
      const res = await getTemplateDrafts();
      setDrafts(res.data?.drafts || []);
    } catch (e) {
      console.error('Error loading drafts:', e);
      setDrafts([]);
    }
  };

  // Save draft to localStorage when form has content
  useEffect(() => {
    if (!showCreateForm) return;
    const hasContent = (createForm.name?.trim() || createForm.body_text?.trim());
    if (hasContent) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...createForm, createStep }));
      } catch (_) {}
    }
  }, [showCreateForm, createForm, createStep]);

  useEffect(() => {
    const matches = String(createForm.body_text || '').match(/\{\{(\d+)\}\}/g) || [];
    const keys = Array.from(new Set(matches.map((m) => String(m).replace(/[^\d]/g, ''))));
    if (keys.length === 0) return;
    setVariableSamples((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        if (!String(next[k] || '').trim()) {
          next[k] = language === 'ar' ? `مثال ${k}` : `Sample ${k}`;
        }
      });
      return next;
    });
  }, [createForm.body_text, language]);

  const getDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  };

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setMetaTokenError(null);
      const res = await getMetaTemplatesAll();
      if (res.data?.error) {
        setMetaTokenError(res.data.error);
        setTemplates([]);
      } else {
      setTemplates(res.data?.templates || []);
      }
    } catch (e) {
      console.error('Error loading templates:', e);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  let filtered = filterStatus === 'all' ? templates : templates.filter((t) => t.status === filterStatus);
  if (filterCategory !== 'all') filtered = filtered.filter((t) => (t.category || '').toUpperCase() === filterCategory);
  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    filtered = filtered.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.label_ar || '').toLowerCase().includes(q) ||
      (t.label_en || '').toLowerCase().includes(q) ||
      (t.body_text || (t.components || []).find((c) => c.type === 'BODY')?.text || '').toLowerCase().includes(q)
    );
  }
  filtered = [...filtered].sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
    if (sortBy === 'category') return (a.category || '').localeCompare(b.category || '');
    return 0;
  });

  const toMs = (value) => {
    if (!value) return NaN;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : NaN;
  };
  const approvalDurationsMs = templates
    .filter((t) => String(t?.status || '').toUpperCase() === 'APPROVED')
    .map((t) => {
      const created = toMs(t.created_time);
      const updated = toMs(t.updated_time);
      return Number.isFinite(created) && Number.isFinite(updated) && updated > created ? (updated - created) : NaN;
    })
    .filter((n) => Number.isFinite(n) && n > 0);
  const typicalApprovalMs = approvalDurationsMs.length > 0
    ? Math.round(approvalDurationsMs.reduce((sum, v) => sum + v, 0) / approvalDurationsMs.length)
    : (24 * 60 * 60 * 1000);
  const pendingAlertThresholdMs = Math.round(typicalApprovalMs * 1.25);
  const isTemplatePendingOverTypical = (template) => {
    if (String(template?.status || '').toUpperCase() !== 'PENDING') return false;
    const created = toMs(template?.created_time);
    if (!Number.isFinite(created)) return false;
    return (Date.now() - created) > pendingAlertThresholdMs;
  };
  const pendingOverTypicalCount = templates.filter((t) => isTemplatePendingOverTypical(t)).length;

  const variableTokens = Array.from(new Set((String(createForm.body_text || '').match(/\{\{(\d+)\}\}/g) || []).map((m) => String(m).replace(/[^\d]/g, ''))));
  const resolvedVariablePreview = String(createForm.body_text || '').replace(/\{\{(\d+)\}\}/g, (_match, idx) => {
    const value = String(variableSamples[idx] || '').trim();
    return value || (language === 'ar' ? `مثال ${idx}` : `Sample ${idx}`);
  });

  const getStatusIcon = (status) => {
    if (status === 'APPROVED') return <FaCheckCircle style={{ color: '#059669', fontSize: '14px' }} />;
    if (status === 'PENDING') return <FaClock style={{ color: '#d97706', fontSize: '14px' }} />;
    if (status === 'REJECTED') return <FaTimesCircle style={{ color: '#dc2626', fontSize: '14px' }} />;
    return null;
  };

  const draftToForm = (d) => {
    if (!d) return EMPTY_CREATE_FORM;
    const isCar = String(d.layout || '').toLowerCase() === 'carousel';
    let cCards = [...EMPTY_CREATE_FORM.carousel_cards];
    let cq = '';
    let cubt = '';
    let cu = '';
    if (isCar && d.carousel_json) {
      try {
        const cj = JSON.parse(d.carousel_json);
        if (Array.isArray(cj.carousel_cards) && cj.carousel_cards.length >= 2) {
          cCards = cj.carousel_cards.map((c) => ({
            media_url: String(c?.media_url || ''),
            url_button_example: String(c?.url_button_example || ''),
          }));
        }
        cq = String(cj.carousel_quick_reply_text || '');
        cubt = String(cj.carousel_url_button_text || '');
        cu = String(cj.carousel_url || '');
      } catch (_) { /* keep defaults */ }
    }
    return {
      ...EMPTY_CREATE_FORM,
      name: d.name || '',
      category: isCar ? 'MARKETING' : (d.category || 'UTILITY'),
      body_text: d.body_text || '',
      layout: isCar ? 'carousel' : 'standard',
      carousel_cards: isCar ? cCards : EMPTY_CREATE_FORM.carousel_cards,
      carousel_quick_reply_text: isCar ? cq : '',
      carousel_url_button_text: isCar ? cubt : '',
      carousel_url: isCar ? cu : '',
      header_format: d.header_format || '',
      header_text: d.header_text || '',
      header_media_url: d.header_media_url || '',
      footer_text: d.footer_text || '',
      button_type: d.button_type || '',
      button_url_text: d.button_url_text || '',
      button_url: d.button_url || '',
      button_phone_text: d.button_phone_text || '',
      button_phone_number: d.button_phone_number || '',
      button_secondary_url_text: d.button_secondary_url_text || '',
      button_secondary_url: d.button_secondary_url || '',
      button_secondary_phone_text: d.button_secondary_phone_text || '',
      button_secondary_phone_number: d.button_secondary_phone_number || '',
      button_quick_1: d.button_quick_1 || '',
      button_quick_2: d.button_quick_2 || '',
      button_quick_3: d.button_quick_3 || '',
    };
  };

  const templateToForm = (t) => {
    const body = t.body_text || (t.components || []).find((c) => c.type === 'BODY')?.text || '';
    const header = (t.components || []).find((c) => c.type === 'HEADER');
    const btns = (t.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [];
    const nameBase = (t.name || 'template').replace(/_(copy(_[0-9]+)?)?$/, '') || 'template';
    const existing = new Set((templates || []).map((x) => (x.name || '').toLowerCase()));
    let copyName = `${nameBase}_copy`;
    for (let n = 2; existing.has(copyName.toLowerCase()); n++) copyName = `${nameBase}_copy_${n}`;
    const form = { ...EMPTY_CREATE_FORM, name: copyName, category: 'UTILITY', body_text: body };
    if (header) {
      form.header_format = header.format || 'IMAGE';
      if (String(form.header_format).toUpperCase() === 'TEXT') {
        form.header_text = header.text || '';
      } else {
      form.header_media_url = header.example?.header_handle?.[0] || '';
    }
    }
    const footer = (t.components || []).find((c) => c.type === 'FOOTER');
    if (footer?.text) form.footer_text = footer.text;
    const ctaButtons = btns.filter((b) => b?.type === 'URL' || b?.type === 'PHONE_NUMBER');
    const b = btns[0];
    if (btns.length > 0 && b?.type === 'QUICK_REPLY') {
      form.button_type = 'quick_reply';
      form.button_quick_1 = btns[0]?.text || '';
      form.button_quick_2 = btns[1]?.text || '';
      form.button_quick_3 = btns[2]?.text || '';
    } else if (ctaButtons.length === 1) {
      if (ctaButtons[0]?.type === 'URL') {
        form.button_type = 'url';
        form.button_url_text = ctaButtons[0]?.text || '';
        form.button_url = ctaButtons[0]?.url || '';
      } else if (ctaButtons[0]?.type === 'PHONE_NUMBER') {
        form.button_type = 'phone';
        form.button_phone_text = ctaButtons[0]?.text || '';
        form.button_phone_number = ctaButtons[0]?.phone_number || '';
      }
    } else if (ctaButtons.length >= 2) {
      const first = ctaButtons[0];
      const second = ctaButtons[1];
      if (first?.type === 'URL' && second?.type === 'PHONE_NUMBER') {
        form.button_type = 'url_phone';
        form.button_url_text = first?.text || '';
        form.button_url = first?.url || '';
        form.button_secondary_phone_text = second?.text || '';
        form.button_secondary_phone_number = second?.phone_number || '';
      } else if (first?.type === 'PHONE_NUMBER' && second?.type === 'URL') {
        form.button_type = 'phone_url';
        form.button_phone_text = first?.text || '';
        form.button_phone_number = first?.phone_number || '';
        form.button_secondary_url_text = second?.text || '';
        form.button_secondary_url = second?.url || '';
      }
    }
    return form;
  };

  const applyStarterTemplate = (starterId) => {
    const starter = STARTER_TEMPLATES.find((t) => t.id === starterId);
    if (!starter) return;
    setEditingFromTemplate(null);
    setEditingDraftId(null);
    setCreateForm((prev) => ({
      ...EMPTY_CREATE_FORM,
      name: starter.name,
      category: starter.category,
      body_text: starter.body_text
    }));
    setCreateStep(1);
  };

  const [carouselCardUploading, setCarouselCardUploading] = useState(null);

  const addCarouselRow = () => {
    setCreateForm((f) => {
      const arr = f.carousel_cards || [];
      if (arr.length >= 10) return f;
      return { ...f, carousel_cards: [...arr, { media_url: '', url_button_example: '' }] };
    });
  };

  const removeCarouselRow = (idx) => {
    setCreateForm((f) => {
      const arr = [...(f.carousel_cards || [])];
      if (arr.length <= 2) return f;
      arr.splice(idx, 1);
      return { ...f, carousel_cards: arr };
    });
  };

  const carouselUrlHasVar = /\{\{1\}\}/.test(String(createForm.carousel_url || ''));

  const getCarouselValidationError = (form) => {
    const cards = Array.isArray(form.carousel_cards) ? form.carousel_cards : [];
    if (cards.length < 2 || cards.length > 10) {
      return language === 'ar' ? 'أضف من بطاقتين إلى ١٠ صور' : 'Add between 2 and 10 image cards';
    }
    for (let i = 0; i < cards.length; i++) {
      if (!String(cards[i]?.media_url || '').trim()) {
        return language === 'ar' ? `صورة البطاقة ${i + 1} مطلوبة` : `Card ${i + 1} needs an image URL`;
      }
    }
    if (!form.carousel_url_button_text?.trim() || !form.carousel_url?.trim()) {
      return language === 'ar' ? 'أدخل نص زر الرابط والرابط الكامل (https)' : 'Enter URL button label and full https URL';
    }
    const urlPat = form.carousel_url.trim();
    if (!/^https?:\/\//i.test(urlPat)) {
      return language === 'ar' ? 'الرابط يجب أن يبدأ بـ https:// أو http://' : 'URL must start with https:// or http://';
    }
    if (/\{\{1\}\}/.test(urlPat)) {
      for (let i = 0; i < cards.length; i++) {
        if (!String(cards[i]?.url_button_example || '').trim()) {
          return language === 'ar' ? `أدخل مثال slug للرابط للبطاقة ${i + 1}` : `Enter a URL slug example for card ${i + 1}`;
        }
      }
    }
    return null;
  };

  const getStatusLabel = (status) => {
    const labels = {
      APPROVED: language === 'ar' ? 'معتمد' : 'Approved',
      PENDING: language === 'ar' ? 'قيد المراجعة' : 'Pending',
      REJECTED: language === 'ar' ? 'مرفوض' : 'Rejected'
    };
    return labels[status] || status;
  };

  const getAgeLabelFromCreated = (createdValue) => {
    const created = toMs(createdValue);
    if (!Number.isFinite(created)) return language === 'ar' ? 'غير متاح' : 'N/A';
    const hours = Math.max(1, Math.round((Date.now() - created) / (60 * 60 * 1000)));
    return language === 'ar' ? `${hours} ساعة` : `${hours}h`;
  };

  const handleCreateSubmit = async () => {
    if (!createForm.name.trim() || !createForm.body_text.trim()) {
      toast.error(language === 'ar' ? 'الاسم والنص مطلوبان' : 'Name and body text are required');
      return;
    }
    const nameErr = validateTemplateName(createForm.name);
    if (nameErr) {
      toast.error(nameErr);
      return;
    }
    if (createForm.body_text.length > BODY_CHAR_LIMIT) {
      toast.error(language === 'ar' ? `نص الرسالة يتجاوز ${BODY_CHAR_LIMIT} حرف. اختصر النص.` : `Message body exceeds ${BODY_CHAR_LIMIT} characters. Shorten it.`);
      return;
    }

    if (createForm.layout === 'carousel') {
      const carErr = getCarouselValidationError(createForm);
      if (carErr) {
        toast.error(carErr);
        return;
      }
      const cards = Array.isArray(createForm.carousel_cards) ? createForm.carousel_cards : [];
      const urlPat = createForm.carousel_url.trim();
      const runCarouselSubmit = async () => {
        setCreating(true);
        try {
          const res = await createMetaTemplate({
            name: createForm.name,
            language: language === 'ar' ? 'ar' : 'en_US',
            category: 'MARKETING',
            body_text: createForm.body_text,
            layout: 'carousel',
            carousel_cards: cards.map((c) => ({
              media_url: String(c.media_url).trim(),
              url_button_example: String(c.url_button_example || '').trim() || undefined,
            })),
            carousel_quick_reply_text: createForm.carousel_quick_reply_text?.trim() || undefined,
            carousel_url_button_text: createForm.carousel_url_button_text.trim(),
            carousel_url: urlPat,
          });
          setShowCreateForm(false);
          setCreateStep(1);
          setEditingFromTemplate(null);
          setCreateForm({ ...EMPTY_CREATE_FORM });
          setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; });
          clearDraft();
          loadTemplates();
          toast.success(res?.data?.message || (language === 'ar' ? 'تم إرسال قالب الكاروسيل للمراجعة في ميتا' : 'Carousel template submitted to Meta for review'));
        } catch (e) {
          let msg = e?.response?.data?.error || e?.message || (language === 'ar' ? 'حدث خطأ' : 'An error occurred');
          msg = String(msg);
          toast.error(msg);
        } finally {
          setCreating(false);
        }
      };
      runCarouselSubmit();
      return;
    }

    const bt = createForm.button_type;
    if (bt === 'url' && (!createForm.button_url_text?.trim() || !createForm.button_url?.trim())) {
      toast.error(language === 'ar' ? 'أدخل نص الزر والرابط' : 'Enter button text and URL');
      return;
    }
    if (bt === 'phone' && (!createForm.button_phone_text?.trim() || !createForm.button_phone_number?.trim())) {
      toast.error(language === 'ar' ? 'أدخل نص الزر ورقم الهاتف' : 'Enter button text and phone number');
      return;
    }
    if (bt === 'quick_reply' && !createForm.button_quick_1?.trim() && !createForm.button_quick_2?.trim() && !createForm.button_quick_3?.trim()) {
      toast.error(language === 'ar' ? 'أدخل زر رد سريع واحد على الأقل' : 'Enter at least one quick reply button');
      return;
    }
    if (bt === 'url_phone' && (
      !createForm.button_url_text?.trim() ||
      !createForm.button_url?.trim() ||
      !createForm.button_secondary_phone_text?.trim() ||
      !createForm.button_secondary_phone_number?.trim()
    )) {
      toast.error(language === 'ar' ? 'أدخل بيانات زر الرابط وزر الاتصال' : 'Enter both URL and phone CTA button details');
      return;
    }
    if (bt === 'phone_url' && (
      !createForm.button_phone_text?.trim() ||
      !createForm.button_phone_number?.trim() ||
      !createForm.button_secondary_url_text?.trim() ||
      !createForm.button_secondary_url?.trim()
    )) {
      toast.error(language === 'ar' ? 'أدخل بيانات زر الاتصال وزر الرابط' : 'Enter both phone and URL CTA button details');
      return;
    }
    try {
      setCreating(true);
      const res = await createMetaTemplate({
        name: createForm.name,
        category: createForm.category,
        body_text: createForm.body_text,
        header_format: createForm.header_format || undefined,
        header_text: createForm.header_text || undefined,
        header_media_url: createForm.header_media_url || undefined,
        footer_text: createForm.footer_text || undefined,
        button_type: createForm.button_type || undefined,
        button_url_text: createForm.button_url_text || undefined,
        button_url: createForm.button_url || undefined,
        button_phone_text: createForm.button_phone_text || undefined,
        button_phone_number: createForm.button_phone_number || undefined,
        button_secondary_url_text: createForm.button_secondary_url_text || undefined,
        button_secondary_url: createForm.button_secondary_url || undefined,
        button_secondary_phone_text: createForm.button_secondary_phone_text || undefined,
        button_secondary_phone_number: createForm.button_secondary_phone_number || undefined,
        button_quick_1: createForm.button_quick_1 || undefined,
        button_quick_2: createForm.button_quick_2 || undefined,
        button_quick_3: createForm.button_quick_3 || undefined
      });
      setShowCreateForm(false);
      setCreateStep(1);
      setEditingFromTemplate(null);
      setCreateForm(EMPTY_CREATE_FORM);
      setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; });
      clearDraft();
      loadTemplates();
      const msg = res?.data?.header_skipped
        ? (language === 'ar' ? res.data.message_ar : res.data.message) || res.data.message
        : (language === 'ar' ? 'تم إرسال القالب للمراجعة' : 'Template submitted for review');
      toast.success(msg);
    } catch (e) {
      let msg = e?.response?.data?.error || e?.message || (language === 'ar' ? 'حدث خطأ' : 'An error occurred');
      msg = String(msg);
      if (msg.toLowerCase().includes('name') && (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('format'))) {
        msg = language === 'ar' ? 'اسم القالب غير صالح. استخدم أحرف إنجليزي صغيرة، أرقام، شرطة سفلية فقط (مثال: property_offer).' : 'Invalid template name. Use lowercase letters, numbers, underscores only (e.g. property_offer).';
      } else if (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already exists')) {
        msg = language === 'ar' ? 'اسم القالب مستخدم مسبقاً. اختر اسماً فريداً أو أضف _copy.' : 'Template name already exists. Choose a unique name or add _copy.';
      } else if ((msg.toLowerCase().includes('body') || msg.toLowerCase().includes('text')) && msg.includes('1024')) {
        const countMatch = msg.match(/current:\s*(\d+)/i);
        const countStr = countMatch ? (language === 'ar' ? ` (الحالي: ${countMatch[1]} حرف)` : ` (current: ${countMatch[1]})`) : '';
        msg = language === 'ar' ? `خطأ في نص الرسالة. تأكد أنه لا يتجاوز ${BODY_CHAR_LIMIT} حرف.${countStr}` : `Message body exceeds ${BODY_CHAR_LIMIT} characters.${countStr}`;
      }
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!createForm.name.trim() || !createForm.body_text.trim()) {
      toast.error(language === 'ar' ? 'الاسم والنص مطلوبان' : 'Name and body text are required');
      return;
    }
    const nameErr = validateTemplateName(createForm.name);
    if (nameErr) {
      toast.error(nameErr);
      return;
    }
    if (createForm.layout === 'carousel') {
      const carErr = getCarouselValidationError(createForm);
      if (carErr) {
        toast.error(carErr);
        return;
      }
    }
    const isCarousel = createForm.layout === 'carousel';
    const carouselJson = isCarousel
      ? JSON.stringify({
        carousel_cards: (createForm.carousel_cards || []).map((c) => ({
          media_url: String(c.media_url || '').trim(),
          url_button_example: String(c.url_button_example || '').trim() || undefined,
        })),
        carousel_quick_reply_text: createForm.carousel_quick_reply_text?.trim() || '',
        carousel_url_button_text: createForm.carousel_url_button_text?.trim() || '',
        carousel_url: createForm.carousel_url?.trim() || '',
      })
      : null;
    const payload = {
      name: createForm.name,
      category: isCarousel ? 'MARKETING' : createForm.category,
      body_text: createForm.body_text,
      layout: isCarousel ? 'carousel' : 'standard',
      carousel_json: carouselJson || undefined,
      header_format: createForm.header_format || undefined,
      header_text: createForm.header_text || undefined,
      header_media_url: createForm.header_media_url || undefined,
      footer_text: createForm.footer_text || undefined,
      button_type: createForm.button_type || undefined,
      button_url_text: createForm.button_url_text || undefined,
      button_url: createForm.button_url || undefined,
      button_phone_text: createForm.button_phone_text || undefined,
      button_phone_number: createForm.button_phone_number || undefined,
      button_secondary_url_text: createForm.button_secondary_url_text || undefined,
      button_secondary_url: createForm.button_secondary_url || undefined,
      button_secondary_phone_text: createForm.button_secondary_phone_text || undefined,
      button_secondary_phone_number: createForm.button_secondary_phone_number || undefined,
      button_quick_1: createForm.button_quick_1 || undefined,
      button_quick_2: createForm.button_quick_2 || undefined,
      button_quick_3: createForm.button_quick_3 || undefined
    };
    try {
      setSavingDraft(true);
      if (editingDraftId) {
        await updateTemplateDraft(editingDraftId, payload);
        toast.success(language === 'ar' ? 'تم تحديث المسودة' : 'Draft updated');
      } else {
        await saveTemplateDraft(payload);
        toast.success(language === 'ar' ? 'تم حفظ المسودة للمراجعة' : 'Draft saved for review');
      }
      setEditingDraftId(null);
      setShowCreateForm(false);
      setCreateForm(EMPTY_CREATE_FORM);
      setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; });
      clearDraft();
      loadDrafts();
    } catch (e) {
      toast.error(e?.response?.data?.error || (language === 'ar' ? 'فشل حفظ المسودة' : 'Failed to save draft'));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitDraft = async () => {
    if (!editingDraftId) return;
    if (createForm.layout === 'carousel') {
      const carErr = getCarouselValidationError(createForm);
      if (carErr) {
        toast.error(carErr);
        return;
      }
    }
    // Pre-flight checks before sending to Meta
    const phoneFields = [createForm.button_phone_number, createForm.button_secondary_phone_number].filter(Boolean);
    for (const ph of phoneFields) {
      const digits = ph.replace(/\D/g, '');
      // Saudi toll-free: 920XXXXXXX (10 digits starting 920) or short numbers
      if (/^9269[2-9]0/.test(digits) || /^9200/.test(digits) || /^920/.test(digits.replace(/^966/, ''))) {
        toast.error(
          language === 'ar'
            ? `رقم الهاتف ${ph} هو رقم مجاني أو خط أرضي. ميتا تتطلب رقم جوال (+966 5XXXXXXXX).`
            : `Phone number ${ph} appears to be a toll-free/landline. Meta requires a mobile number (+966 5XXXXXXXX).`
        );
        return;
      }
    }
    try {
      setSubmittingDraft(true);
      const res = await submitTemplateDraft(editingDraftId);
      setEditingDraftId(null);
      setShowCreateForm(false);
      setCreateForm(EMPTY_CREATE_FORM);
      setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; });
      clearDraft();
      loadDrafts();
      loadTemplates();
      const msg = res?.data?.header_skipped
        ? (language === 'ar' ? res.data.message_ar : res.data.message) || res.data.message
        : (language === 'ar' ? 'تم إرسال القالب لميتا للمراجعة' : 'Template submitted to Meta for review');
      toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.error || (language === 'ar' ? 'فشل الإرسال' : 'Failed to submit'));
    } finally {
      setSubmittingDraft(false);
    }
  };

  const handleEditDraft = async (draft) => {
    setEditingDraftId(draft.id);
    setCreateForm(draftToForm(draft));
    setHeaderPreviewUrl(draft.header_media_url || '');
    setShowCreateForm(true);
    setCreateStep(1);
  };

  const handleDeleteDraft = async (id) => {
    if (!window.confirm(language === 'ar' ? 'حذف هذه المسودة؟' : 'Delete this draft?')) return;
    try {
      await deleteTemplateDraft(id);
      if (editingDraftId === id) {
        setEditingDraftId(null);
        setShowCreateForm(false);
        setCreateForm(EMPTY_CREATE_FORM);
      }
      loadDrafts();
      toast.success(language === 'ar' ? 'تم حذف المسودة' : 'Draft deleted');
    } catch (e) {
      toast.error(e?.response?.data?.error || (language === 'ar' ? 'فشل الحذف' : 'Failed to delete'));
    }
  };

  if (loading) {
    return (
      <div className="wa-loading">
        <div className="wa-loading-spinner" />
        <p className="wa-loading-text">
          {language === 'ar' ? 'جاري تحميل القوالب...' : 'Loading templates...'}
        </p>
      </div>
    );
  }

  return (
    <div className="wa-animate-in" dir={language === 'ar' ? 'rtl' : 'ltr'} style={{ padding: '24px' }}>
      {metaTokenError && (
        <div style={{ marginBottom: '20px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '22px', lineHeight: 1 }}>🔑</span>
          <div>
            <div style={{ fontWeight: 700, color: '#9a3412', fontSize: '14px', marginBottom: '4px' }}>
              {language === 'ar' ? 'تعذّر الاتصال بـ Meta — التوكن منتهي أو غير صالح' : 'Cannot connect to Meta — token expired or invalid'}
            </div>
            <div style={{ fontSize: '12px', color: '#c2410c' }}>
              {language === 'ar'
                ? 'يرجى تحديث Access Token في إعدادات واتساب ← تعديل التكامل ← أدخل توكناً جديداً من Meta Business Suite.'
                : 'Please update the Access Token in WhatsApp Settings → Edit integration → paste a new token from Meta Business Suite.'}
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaFileAlt style={{ color: '#f97316' }} />
          {language === 'ar' ? 'إدارة القوالب' : 'Template Management'}
        </h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={() => { if (showCreateForm) { setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; }); setEditingDraftId(null); } setShowCreateForm((p) => !p); }} className={showCreateForm ? 'wa-btn wa-btn-secondary' : 'wa-btn wa-btn-primary'} style={{ padding: '10px 18px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: !showCreateForm ? '0 2px 8px rgba(249,115,22,0.25)' : 'none' }}>
            <FaPlus />
            {showCreateForm ? (language === 'ar' ? 'إغلاق' : 'Close') : (language === 'ar' ? 'إنشاء قالب' : 'Create template')}
          </button>
          <button type="button" onClick={loadTemplates} className="wa-btn wa-btn-secondary" style={{ padding: '10px 20px', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s ease', borderColor: '#e2e8f0' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = ''; }}>
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </button>
          <div style={{ display: 'flex', gap: '6px', padding: '4px', background: '#f1f5f9', borderRadius: '9999px', border: '1px solid #e2e8f0' }}>
          {['all', 'APPROVED', 'PENDING', 'REJECTED'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
                onMouseEnter={(e) => { if (filterStatus !== s) { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.color = '#ea580c'; } }}
                onMouseLeave={(e) => { if (filterStatus !== s) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
              style={{
                  padding: '8px 14px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '9999px',
                  background: filterStatus === s ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : 'transparent',
                  color: filterStatus === s ? '#fff' : '#64748b',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: filterStatus === s ? '0 2px 6px rgba(249,115,22,0.25)' : 'none'
              }}
            >
              {s === 'all' ? (language === 'ar' ? 'الكل' : 'All') : getStatusLabel(s)}
            </button>
          ))}
        </div>
        </div>
      </div>

      {drafts.length > 0 && (
        <div style={{ marginBottom: '20px', padding: '16px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaSave style={{ fontSize: '16px' }} />
            {language === 'ar' ? 'مسودات للمراجعة' : 'Drafts for review'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {drafts.map((d) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #fcd34d', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <span style={{ fontWeight: '600', fontSize: '13px', color: '#0f172a' }}>{d.name}</span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>{d.category}</span>
                <button type="button" onClick={() => handleEditDraft(d)} className="wa-btn wa-btn-secondary" style={{ padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }}>
                  <FaEye />
                  {language === 'ar' ? 'تعديل' : 'Edit'}
                </button>
                <button type="button" onClick={() => handleDeleteDraft(d.id)} className="wa-btn wa-btn-secondary" style={{ padding: '4px 10px', fontSize: '11px', color: '#dc2626', borderColor: '#fecaca' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#dc2626'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = '#fecaca'; }}>
                  <FaTrashAlt />
                </button>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '12px', color: '#92400e', marginTop: '10px', marginBottom: 0 }}>
            {language === 'ar' ? 'احفظ القالب كمسودة للمراجعة قبل إرساله لميتا. يمكن للمدير أو المالك إرساله للموافقة.' : 'Save templates as drafts for management review before submitting to Meta. Owner/manager can submit for approval.'}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="search" placeholder={language === 'ar' ? 'بحث بالاسم أو النص...' : 'Search by name or text...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="wa-input" style={{ maxWidth: '240px', padding: '8px 12px', fontSize: '13px' }} aria-label={language === 'ar' ? 'بحث القوالب' : 'Search templates'} />
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="wa-input" style={{ width: 'auto', minWidth: '120px', padding: '8px 12px', fontSize: '12px' }} aria-label={language === 'ar' ? 'نوع القالب' : 'Template category'}>
          <option value="all">{language === 'ar' ? 'كل الأنواع' : 'All categories'}</option>
          <option value="UTILITY">{language === 'ar' ? 'خدمة' : 'Utility'}</option>
          <option value="MARKETING">{language === 'ar' ? 'تسويق' : 'Marketing'}</option>
          <option value="AUTHENTICATION">{language === 'ar' ? 'مصادقة' : 'Authentication'}</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="wa-input" style={{ width: 'auto', minWidth: '100px', padding: '8px 12px', fontSize: '12px' }} aria-label={language === 'ar' ? 'ترتيب' : 'Sort by'}>
          <option value="name">{language === 'ar' ? 'الاسم' : 'Name'}</option>
          <option value="status">{language === 'ar' ? 'الحالة' : 'Status'}</option>
          <option value="category">{language === 'ar' ? 'النوع' : 'Category'}</option>
        </select>
      </div>

      <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
        {language === 'ar'
          ? 'تُستخدم للحملات والرسائل خارج 24 ساعة'
          : 'Used for campaigns and messages outside 24h'}
        {(searchQuery.trim() || filterCategory !== 'all' || filterStatus !== 'all') && (
          <span style={{ marginRight: '8px', fontWeight: '600' }}>
            {language === 'ar' ? `(${filtered.length} من ${templates.length})` : `(${filtered.length} of ${templates.length})`}
          </span>
        )}
      </p>
      <div style={{ marginBottom: '14px', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '12px', color: '#475569' }}>
        {language === 'ar'
          ? `متوسط وقت الاعتماد المعتاد: ${Math.max(1, Math.round(typicalApprovalMs / (60 * 60 * 1000)))} ساعة.`
          : `Typical approval average: ${Math.max(1, Math.round(typicalApprovalMs / (60 * 60 * 1000)))}h.`}
        {' '}
        {language === 'ar'
          ? `المعلّق المتجاوز للمتوسط: ${pendingOverTypicalCount}`
          : `Pending beyond typical average: ${pendingOverTypicalCount}`}
      </div>

      {showCreateForm && (
        <div style={{ marginBottom: '24px', padding: '24px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', borderLeft: '4px solid #f97316', boxShadow: '0 2px 12px rgba(249,115,22,0.08)' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>{language === 'ar' ? 'إنشاء قالب جديد' : 'Create new template'}</h3>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>
            {language === 'ar' ? 'اختر نقطة البداية أدناه' : 'Choose a starting point below'}
          </p>
          {/* Preview — compact border, larger phone */}
          <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', borderLeft: '3px solid #f97316', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#0f172a', marginBottom: '8px' }}>
              {language === 'ar' ? 'معاينة الرسالة' : 'Message preview'}
            </div>
            {editingFromTemplate && (
              <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', fontSize: '12px', color: '#15803d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaCopy style={{ fontSize: '14px', flexShrink: 0 }} />
                <span>{language === 'ar' ? `تعديل من "${editingFromTemplate.name}"` : `Editing from "${editingFromTemplate.name}"`}</span>
                <button type="button" onClick={() => setEditingFromTemplate(null)} style={{ marginRight: 'auto', padding: '2px 8px', fontSize: '11px', background: 'transparent', border: '1px solid #22c55e', borderRadius: '4px', color: '#15803d', cursor: 'pointer' }}>{language === 'ar' ? 'إخفاء' : 'Dismiss'}</button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <WhatsAppTemplatePreview
                key="preview-create"
                bodyText={createForm.body_text}
                layout={createForm.layout}
                carouselCards={createForm.carousel_cards}
                carouselQuickReplyText={createForm.carousel_quick_reply_text}
                carouselUrlButtonText={createForm.carousel_url_button_text}
                carouselUrl={createForm.carousel_url}
                headerFormat={createForm.header_format}
                headerText={createForm.header_text}
                headerMediaUrl={headerPreviewUrl || createForm.header_media_url || headerMediaRef.current}
                footerText={createForm.footer_text}
                createStep={createStep}
                buttonType={createForm.button_type ?? ''}
                buttonUrlText={createForm.button_url_text ?? ''}
                buttonUrl={createForm.button_url ?? ''}
                buttonPhoneText={createForm.button_phone_text ?? ''}
                buttonPhoneNumber={createForm.button_phone_number ?? ''}
                buttonSecondaryUrlText={createForm.button_secondary_url_text ?? ''}
                buttonSecondaryUrl={createForm.button_secondary_url ?? ''}
                buttonSecondaryPhoneText={createForm.button_secondary_phone_text ?? ''}
                buttonSecondaryPhoneNumber={createForm.button_secondary_phone_number ?? ''}
                buttonQuick={[createForm.button_quick_1, createForm.button_quick_2, createForm.button_quick_3].filter((v) => v != null && String(v).trim() !== '')}
                language={language}
                showSampleSelector={true}
                phoneFrame={true}
              />
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
          <div style={{ display: 'grid', gap: '8px', marginBottom: '20px' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
              {language === 'ar'
                ? 'مكتبة بداية مختصرة (5 فقط): 2 تسويق + 2 خدمة + 1 رسالة أولى'
                : 'Compact starter library (5 only): 2 marketing + 2 service + 1 first contact'}
              </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <select
                className="wa-input"
                value={selectedStarterTemplateId}
                onChange={(e) => setSelectedStarterTemplateId(e.target.value)}
                style={{ minWidth: '280px', maxWidth: '100%', padding: '8px 12px', fontSize: '12px' }}
              >
                <option value="">{language === 'ar' ? 'اختر قالب بداية جاهز' : 'Select a starter template'}</option>
                {STARTER_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {language === 'ar' ? t.nameAr : t.nameEn}
                  </option>
                ))}
              </select>
                  <button
                    type="button"
                className="wa-btn wa-btn-secondary"
                disabled={!selectedStarterTemplateId}
                onClick={() => applyStarterTemplate(selectedStarterTemplateId)}
                style={{ padding: '8px 12px', fontSize: '12px' }}
              >
                {language === 'ar' ? 'تطبيق القالب' : 'Apply starter'}
                  </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              <select
                className="wa-input"
                value={selectedApprovedCopyName}
                onChange={(e) => setSelectedApprovedCopyName(e.target.value)}
                style={{ minWidth: '280px', maxWidth: '100%', padding: '8px 12px', fontSize: '12px' }}
              >
                <option value="">{language === 'ar' ? 'أو انسخ من قالب معتمد' : 'Or copy from approved template'}</option>
                {templates.filter((t) => t.status === 'APPROVED').slice(0, 8).map((t) => (
                  <option key={`copy-${t.name}`} value={t.name}>{t.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="wa-btn wa-btn-secondary"
                disabled={!selectedApprovedCopyName}
                onClick={() => {
                  const base = templates.find((t) => t.name === selectedApprovedCopyName);
                  if (!base) return;
                  setEditingFromTemplate({ name: base.name });
                  setEditingDraftId(null);
                  setCreateForm(templateToForm(base));
                  setCreateStep(3);
                }}
                style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FaCopy style={{ fontSize: '10px' }} />
                {language === 'ar' ? 'نسخ كأساس' : 'Copy as base'}
              </button>
            </div>
            <button type="button" onClick={() => { setEditingFromTemplate(null); setEditingDraftId(null); setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; }); setCreateForm(EMPTY_CREATE_FORM); setCreateStep(1); clearDraft(); }} className="wa-btn wa-btn-secondary" onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#ea580c'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '8px 12px', fontSize: '12px', whiteSpace: 'nowrap', borderStyle: 'dashed', transition: 'all 0.2s ease' }}>
              {language === 'ar' ? 'ابدأ من الصفر' : 'Start from scratch'}
            </button>
            {getDraft() && !createForm.body_text?.trim() && !createForm.name?.trim() && (
              <button
                type="button"
                onClick={() => {
                  const d = getDraft();
                  if (d) {
                    const { createStep: savedStep, ...rest } = d;
                    const isCar = rest.layout === 'carousel';
                    const cards = Array.isArray(rest.carousel_cards) && rest.carousel_cards.length >= 2
                      ? rest.carousel_cards
                      : EMPTY_CREATE_FORM.carousel_cards;
                    setCreateForm({
                      ...EMPTY_CREATE_FORM,
                      ...rest,
                      layout: isCar ? 'carousel' : 'standard',
                      category: isCar ? 'MARKETING' : (rest.category || 'UTILITY'),
                      carousel_cards: isCar ? cards : EMPTY_CREATE_FORM.carousel_cards,
                      carousel_quick_reply_text: isCar ? (rest.carousel_quick_reply_text || '') : '',
                      carousel_url_button_text: isCar ? (rest.carousel_url_button_text || '') : '',
                      carousel_url: isCar ? (rest.carousel_url || '') : '',
                    });
                    setCreateStep(savedStep || 1);
                  }
                }}
                className="wa-btn wa-btn-secondary"
                style={{ padding: '8px 12px', fontSize: '12px', whiteSpace: 'nowrap', background: '#fef3c7', borderColor: '#f59e0b', color: '#b45309' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fde68a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fef3c7'; }}
              >
                {language === 'ar' ? '📋 استعادة المسودة' : '📋 Restore draft'}
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gap: '12px', maxWidth: '520px' }}>
            <div key="step1" style={{ display: 'grid', gap: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '4px', paddingBottom: '6px', borderBottom: '2px solid #f97316' }}>1. {language === 'ar' ? 'الرسالة' : 'Message'}</div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                  {language === 'ar' ? 'اسم القالب' : 'Template name'}
                </label>
                <input type="text" className="wa-input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="property_offer" style={{ width: '100%', padding: '8px 12px', borderColor: validateTemplateName(createForm.name) ? '#dc2626' : undefined }} aria-invalid={!!validateTemplateName(createForm.name)} aria-describedby={validateTemplateName(createForm.name) ? 'name-error' : undefined} />
                <small style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                  {language === 'ar'
                    ? 'يمكن كتابة الاسم بالعربية. سيتم تحويله تلقائياً إلى صيغة ميتا الآمنة (lowercase_with_underscore).'
                    : 'Arabic names are supported. They are auto-normalized to Meta-safe lowercase_with_underscore format.'}
                </small>
                {String(createForm.name || '').trim() && (
                  <small style={{ fontSize: '11px', color: '#0f766e', display: 'block', marginTop: '2px' }}>
                    {language === 'ar'
                      ? `الاسم بعد التحويل المتوقع: ${normalizeTemplateNamePreview(createForm.name)}`
                      : `Expected normalized key: ${normalizeTemplateNamePreview(createForm.name)}`}
                  </small>
                )}
                {(() => {
                  const suggested = suggestTemplateNameFromBody(createForm.body_text);
                  const showSuggestion = suggested && createForm.name.trim() !== suggested;
                  return showSuggestion ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{language === 'ar' ? 'اقتراح:' : 'Suggested:'}</span>
                      <button type="button" onClick={() => setCreateForm({ ...createForm, name: suggested })} className="wa-btn wa-btn-secondary" style={{ padding: '4px 10px', fontSize: '11px', background: '#fff7ed', borderColor: '#f97316', color: '#ea580c' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#ffedd5'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#fff7ed'; }}>
                        {suggested}
                      </button>
                    </div>
                  ) : null;
                })()}
                {validateTemplateName(createForm.name) && <small id="name-error" style={{ fontSize: '11px', color: '#dc2626', display: 'block', marginTop: '4px' }}>{validateTemplateName(createForm.name)}</small>}
                {!validateTemplateName(createForm.name) && <small style={{ fontSize: '11px', color: '#94a3b8', display: 'block', marginTop: '4px' }}>{language === 'ar' ? 'مثال: property_offer' : 'e.g. property_offer'}</small>}
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>{language === 'ar' ? 'نوع القالب' : 'Template type'}</label>
                <select
                  className="wa-input"
                  value={createForm.layout === 'carousel' ? 'MARKETING' : createForm.category}
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  disabled={createForm.layout === 'carousel'}
                  style={{ width: '100%', padding: '8px 12px', opacity: createForm.layout === 'carousel' ? 0.75 : 1 }}
                >
                  <option value="UTILITY">{language === 'ar' ? 'خدمة' : 'Service'}</option>
                  <option value="MARKETING">{language === 'ar' ? 'تسويق' : 'Marketing'}</option>
                  <option value="AUTHENTICATION">{language === 'ar' ? 'مصادقة' : 'Authentication'}</option>
                </select>
                {createForm.layout === 'carousel' && (
                  <small style={{ fontSize: '11px', color: '#b45309', display: 'block', marginTop: '6px' }}>
                    {language === 'ar' ? 'قوالب الكاروسيل تسويقية فقط (ميتا).' : 'Carousel templates are marketing-only (Meta).'}
                  </small>
                )}
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '6px' }}>{language === 'ar' ? 'تنسيق الرسالة' : 'Message layout'}</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={createForm.layout === 'standard' ? 'wa-btn wa-btn-primary' : 'wa-btn wa-btn-secondary'}
                    onClick={() => setCreateForm({ ...createForm, layout: 'standard' })}
                    style={{ padding: '8px 14px', fontSize: '12px' }}
                  >
                    {language === 'ar' ? 'عادي (رأس الرسالة + النص)' : 'Standard (header + body)'}
                  </button>
                  <button
                    type="button"
                    className={createForm.layout === 'carousel' ? 'wa-btn wa-btn-primary' : 'wa-btn wa-btn-secondary'}
                    onClick={() => setCreateForm({ ...createForm, layout: 'carousel', category: 'MARKETING' })}
                    style={{ padding: '8px 14px', fontSize: '12px' }}
                  >
                    {language === 'ar' ? '🖼 كاروسيل صور (٢–١٠)' : '🖼 Image carousel (2–10)'}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '6px' }}>{language === 'ar' ? 'نص الرسالة' : 'Message text'}</label>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', letterSpacing: '0.02em' }}>{language === 'ar' ? '▪ بيانات العميل' : '▪ Client data'}</span>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {VARIABLE_OPTIONS.map((opt) => (
                      <button key={opt.value} type="button" onClick={() => setCreateForm({ ...createForm, body_text: createForm.body_text + opt.value })} className="wa-btn wa-btn-secondary" style={{ padding: '6px 12px', fontSize: '12px', background: '#f0fdf4', borderColor: '#86efac', color: '#15803d' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#4ade80'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#86efac'; }}>
                        + {language === 'ar' ? opt.labelAr : opt.labelEn}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: '#334155', display: 'block', marginBottom: '6px', letterSpacing: '0.02em' }}>{language === 'ar' ? '▪ عبارات جاهزة' : '▪ Ready phrases'}</span>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {BODY_SNIPPETS.map((s) => (
                      <button key={s.shortAr} type="button" onClick={() => setCreateForm({ ...createForm, body_text: createForm.body_text + (language === 'ar' ? s.textAr : s.textEn) })} className="wa-btn wa-btn-secondary" style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap' }} title={language === 'ar' ? s.textAr : s.textEn}>
                      {language === 'ar' ? s.shortAr : s.shortEn}
                    </button>
                  ))}
                  </div>
                </div>
                <textarea className="wa-input" value={createForm.body_text} onChange={(e) => setCreateForm({ ...createForm, body_text: e.target.value })} placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Type your message here...'} rows={7} style={{ width: '100%', padding: '10px 12px', resize: 'vertical', minHeight: '160px', borderColor: createForm.body_text.length > BODY_CHAR_LIMIT ? '#dc2626' : undefined }} aria-invalid={createForm.body_text.length > BODY_CHAR_LIMIT} />
                <div style={{ marginTop: '6px' }}>
                  <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '9999px', overflow: 'hidden', marginBottom: '4px' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(100, (createForm.body_text.length / BODY_CHAR_LIMIT) * 100)}%`,
                        background: createForm.body_text.length > BODY_CHAR_LIMIT ? '#dc2626' : createForm.body_text.length > BODY_CHAR_LIMIT * 0.8 ? '#d97706' : '#22c55e',
                        borderRadius: '9999px',
                        transition: 'width 0.2s ease, background 0.2s ease'
                      }}
                    />
                  </div>
                  <small style={{ fontSize: '11px', color: createForm.body_text.length > BODY_CHAR_LIMIT ? '#dc2626' : '#94a3b8' }}>{createForm.body_text.length > BODY_CHAR_LIMIT ? (language === 'ar' ? `تجاوز الحد (${BODY_CHAR_LIMIT})` : `Over limit (${BODY_CHAR_LIMIT})`) : `${createForm.body_text.length} / ${BODY_CHAR_LIMIT}`}</small>
                </div>
                {variableTokens.length > 0 && (
                  <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
                      {language === 'ar' ? 'معاينة المتغيرات (مبسطة)' : 'Simple variable preview'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '8px' }}>
                      {variableTokens.map((idx) => (
                        <div key={idx}>
                          <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                            {`{{${idx}}}`}
                          </label>
                          <input
                            type="text"
                            className="wa-input"
                            value={variableSamples[idx] || ''}
                            onChange={(e) => setVariableSamples((prev) => ({ ...prev, [idx]: e.target.value }))}
                            style={{ width: '100%', padding: '7px 10px', fontSize: '12px' }}
                          />
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: '#0f172a', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 10px', lineHeight: 1.6, direction: detectDirection(resolvedVariablePreview), textAlign: detectDirection(resolvedVariablePreview) === 'rtl' ? 'right' : 'left', wordBreak: 'break-word' }}>
                      {(() => {
                        const raw = resolvedVariablePreview;
                        if (!raw) return null;
                        const lines = String(raw).split('\n');
                        const out = [];
                        for (let li = 0; li < lines.length; li++) {
                          if (li > 0) out.push(<br key={`br${li}`} />);
                          const line = lines[li];
                          const re = /\*([^*]+)\*|_([^_]+)_|~([^~]+)~/g;
                          let cursor = 0; let m;
                          while ((m = re.exec(line)) !== null) {
                            if (m.index > cursor) out.push(<span key={`${li}p${cursor}`}>{line.slice(cursor, m.index)}</span>);
                            if (m[1] !== undefined) out.push(<strong key={`${li}b${m.index}`} style={{ fontWeight: 700 }}>{m[1]}</strong>);
                            else if (m[2] !== undefined) out.push(<em key={`${li}i${m.index}`}>{m[2]}</em>);
                            else if (m[3] !== undefined) out.push(<s key={`${li}s${m.index}`}>{m[3]}</s>);
                            cursor = m.index + m[0].length;
                          }
                          if (cursor < line.length) out.push(<span key={`${li}p${cursor}`}>{line.slice(cursor)}</span>);
                        }
                        return out;
                      })()}
              </div>
            </div>
            )}
              </div>
              {createForm.layout === 'carousel' && (
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #f97316' }}>
                    {language === 'ar' ? 'بطاقات الكاروسيل (٢–١٠ صور)' : 'Carousel cards (2–10 images)'}
                  </div>
                  <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '12px', lineHeight: 1.5 }}>
                    {language === 'ar'
                      ? 'كل بطاقة تحتاج صورة. أزرار الرابط و(اختياري) الرد السريع مشتركة لكل البطاقات. للروابط الديناميكية ضع {{1}} في الرابط وأدخل مثال slug لكل بطاقة.'
                      : 'Each card needs an image. URL and optional quick-reply are shared across cards. For dynamic links put {{1}} in the URL and enter a slug example per card.'}
                  </p>
                  {(createForm.carousel_cards || []).map((card, idx) => (
                    <div key={idx} style={{ marginBottom: '14px', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155' }}>
                          {language === 'ar' ? `بطاقة ${idx + 1}` : `Card ${idx + 1}`}
                        </span>
                        {(createForm.carousel_cards || []).length > 2 && (
                          <button type="button" className="wa-btn wa-btn-secondary" onClick={() => removeCarouselRow(idx)} style={{ padding: '4px 10px', fontSize: '11px' }}>
                            {language === 'ar' ? 'حذف' : 'Remove'}
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <label className="wa-btn wa-btn-secondary" style={{ padding: '8px 14px', fontSize: '12px', cursor: carouselCardUploading === idx ? 'wait' : 'pointer', margin: 0, display: 'flex', alignItems: 'center', gap: '6px', opacity: carouselCardUploading === idx ? 0.7 : 1 }}>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            disabled={carouselCardUploading === idx}
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              setCarouselCardUploading(idx);
                              try {
                                const r = await uploadWhatsAppMedia(f);
                                const url = r.data?.url || '';
                                setCreateForm((prev) => {
                                  const arr = [...(prev.carousel_cards || [])];
                                  arr[idx] = { ...arr[idx], media_url: url };
                                  return { ...prev, carousel_cards: arr };
                                });
                              } catch (err) {
                                toast.error(err?.response?.data?.error || (language === 'ar' ? 'فشل الرفع' : 'Upload failed'));
                              } finally {
                                setCarouselCardUploading(null);
                                e.target.value = '';
                              }
                            }}
                          />
                          {carouselCardUploading === idx ? (language === 'ar' ? 'جاري الرفع...' : 'Uploading...') : (language === 'ar' ? '📤 رفع صورة' : '📤 Upload image')}
                        </label>
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'أو رابط الصورة' : 'Or image URL'}</div>
                      <input
                        type="url"
                        className="wa-input"
                        value={card.media_url || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCreateForm((prev) => {
                            const arr = [...(prev.carousel_cards || [])];
                            arr[idx] = { ...arr[idx], media_url: v };
                            return { ...prev, carousel_cards: arr };
                          });
                        }}
                        placeholder="https://..."
                        style={{ width: '100%', padding: '8px 12px', marginBottom: carouselUrlHasVar ? '8px' : 0 }}
                      />
                      {carouselUrlHasVar && (
                        <div>
                          <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                            {language === 'ar' ? `مثال الجزء الديناميكي في الرابط (للبطاقة ${idx + 1})` : `URL variable example for card ${idx + 1}`}
                          </label>
                          <input
                            type="text"
                            className="wa-input"
                            value={card.url_button_example || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setCreateForm((prev) => {
                                const arr = [...(prev.carousel_cards || [])];
                                arr[idx] = { ...arr[idx], url_button_example: v };
                                return { ...prev, carousel_cards: arr };
                              });
                            }}
                            placeholder={language === 'ar' ? 'مثال: villa-12' : 'e.g. listing-abc'}
                            style={{ width: '100%', padding: '8px 12px' }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {(createForm.carousel_cards || []).length < 10 && (
                    <button type="button" className="wa-btn wa-btn-secondary" onClick={addCarouselRow} style={{ padding: '8px 14px', fontSize: '12px', marginBottom: '16px' }}>
                      {language === 'ar' ? '+ إضافة بطاقة' : '+ Add card'}
                    </button>
                  )}
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '8px', marginTop: '8px' }}>{language === 'ar' ? 'الأزرار المشتركة' : 'Shared buttons'}</div>
                  <input
                    type="text"
                    className="wa-input"
                    value={createForm.carousel_quick_reply_text}
                    onChange={(e) => setCreateForm({ ...createForm, carousel_quick_reply_text: e.target.value })}
                    placeholder={language === 'ar' ? 'نص زر الرد السريع (اختياري)' : 'Quick reply label (optional)'}
                    style={{ width: '100%', padding: '8px 12px', marginBottom: '8px' }}
                  />
                  <input
                    type="text"
                    className="wa-input"
                    value={createForm.carousel_url_button_text}
                    onChange={(e) => setCreateForm({ ...createForm, carousel_url_button_text: e.target.value })}
                    placeholder={language === 'ar' ? 'نص زر الرابط (مطلوب)' : 'URL button label (required)'}
                    style={{ width: '100%', padding: '8px 12px', marginBottom: '8px' }}
                  />
                  <input
                    type="url"
                    className="wa-input"
                    value={createForm.carousel_url}
                    onChange={(e) => setCreateForm({ ...createForm, carousel_url: e.target.value })}
                    placeholder="https://example.com/p/{{1}}"
                    style={{ width: '100%', padding: '8px 12px' }}
                  />
                </div>
              )}
            </div>
            {createForm.layout !== 'carousel' && (
            <>
            <div key="step2" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '8px', paddingBottom: '6px', borderBottom: '2px solid #f97316' }}>2. {language === 'ar' ? 'رأس الرسالة والتذييل (اختياري)' : 'Header & footer (optional)'}</div>
              <select className="wa-input" value={createForm.header_format} onChange={(e) => {
                const v = e.target.value;
                if (!v) setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; });
                setCreateForm({
                  ...createForm,
                  header_format: v,
                  header_text: v === 'TEXT' ? createForm.header_text : '',
                  header_media_url: (v && v !== 'TEXT') ? createForm.header_media_url : ''
                });
              }} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }}>
                <option value="">{language === 'ar' ? 'لا' : 'No'}</option>
                <option value="TEXT">{language === 'ar' ? 'نص' : 'Text'}</option>
                <option value="IMAGE">{language === 'ar' ? 'صورة' : 'Image'}</option>
                <option value="VIDEO">{language === 'ar' ? 'فيديو' : 'Video'}</option>
                <option value="DOCUMENT">{language === 'ar' ? 'مستند' : 'Document'}</option>
              </select>
              {createForm.header_format === 'TEXT' && (
                <input
                  type="text"
                  className="wa-input"
                  value={createForm.header_text}
                  onChange={(e) => setCreateForm({ ...createForm, header_text: e.target.value })}
                  placeholder={language === 'ar' ? 'نص رأس الرسالة (حتى 60 حرفاً)' : 'Header text (max 60 chars)'}
                  style={{ width: '100%', padding: '8px 12px', marginBottom: '8px' }}
                />
              )}
              {createForm.header_format && createForm.header_format !== 'TEXT' && (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <label className="wa-btn wa-btn-secondary" onMouseEnter={(e) => { if (!headerUploading) { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; } }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '8px 14px', fontSize: '12px', cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s ease' }}>
                      <input type="file" accept={createForm.header_format === 'IMAGE' ? 'image/*' : createForm.header_format === 'VIDEO' ? 'video/*' : 'application/pdf,.pdf'} style={{ display: 'none' }} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; setHeaderPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); }); setHeaderUploading(true); try { const r = await uploadWhatsAppMedia(f); const url = r.data?.url || ''; setCreateForm((prev) => ({ ...prev, header_media_url: url })); } catch (err) { toast.error(err?.response?.data?.error || (language === 'ar' ? 'فشل الرفع' : 'Upload failed')); } finally { setHeaderUploading(false); e.target.value = ''; } }} disabled={headerUploading} />
                      {headerUploading ? (language === 'ar' ? 'جاري الرفع...' : 'Uploading...') : (language === 'ar' ? '📤 رفع ملف' : '📤 Upload file')}
                    </label>
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'أو الرابط:' : 'Or URL:'}</div>
                  <input type="url" className="wa-input" value={createForm.header_media_url} onChange={(e) => { setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; }); setCreateForm({ ...createForm, header_media_url: e.target.value }); }} placeholder="https://..." style={{ width: '100%', padding: '8px 12px' }} />
                  <div style={{ fontSize: '11px', color: '#b45309', marginTop: '6px', padding: '8px 10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                    {language === 'ar' ? 'للموافقة من ميتا: استخدم رابط صورة عام (مثل nufouth.com أو imgur). الروابط من التطبيق قد لا تعمل.' : 'For Meta approval: use a public image URL (e.g. from nufouth.com or imgur). App upload URLs may not work.'}
                  </div>
                </>
              )}
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'نص التذييل (اختياري)' : 'Footer text (optional)'}</div>
                <input
                  type="text"
                  className="wa-input"
                  value={createForm.footer_text}
                  onChange={(e) => setCreateForm({ ...createForm, footer_text: e.target.value })}
                  placeholder={language === 'ar' ? 'مثال: للرد أكتب نعم' : 'e.g. Reply YES to continue'}
                  style={{ width: '100%', padding: '8px 12px' }}
                />
            </div>
            </div>
            <div key="step3" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', marginBottom: '8px', paddingBottom: '6px', borderBottom: '2px solid #f97316' }}>3. {language === 'ar' ? 'الأزرار (اختياري)' : 'Buttons (optional)'}</div>
              <select className="wa-input" value={createForm.button_type} onChange={(e) => setCreateForm({
                ...createForm,
                button_type: e.target.value,
                button_url_text: '',
                button_url: '',
                button_phone_text: '',
                button_phone_number: '',
                button_secondary_url_text: '',
                button_secondary_url: '',
                button_secondary_phone_text: '',
                button_secondary_phone_number: '',
                button_quick_1: '',
                button_quick_2: '',
                button_quick_3: ''
              })} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }}>
                <option value="">{language === 'ar' ? 'بدون زر' : 'No button'}</option>
                <option value="url">{language === 'ar' ? '🔗 رابط' : '🔗 Link'}</option>
                <option value="phone">{language === 'ar' ? '📞 اتصال' : '📞 Call'}</option>
                <option value="url_phone">{language === 'ar' ? '🔗 رابط + 📞 اتصال' : '🔗 URL + 📞 Phone'}</option>
                <option value="phone_url">{language === 'ar' ? '📞 اتصال + 🔗 رابط' : '📞 Phone + 🔗 URL'}</option>
                <option value="quick_reply">{language === 'ar' ? '💬 رد سريع' : '💬 Quick reply'}</option>
              </select>
              {(createForm.button_type === 'url_phone' || createForm.button_type === 'phone_url') && (
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>
                  {language === 'ar' ? 'يمكنك إضافة زرين CTA كحد أقصى (متوافق مع ميتا).' : 'You can add up to two CTA buttons (Meta-compatible).'}
                </div>
              )}
              {createForm.button_type === 'url' && (
                <>
                  <input type="text" className="wa-input" value={createForm.button_url_text} onChange={(e) => setCreateForm({ ...createForm, button_url_text: e.target.value })} placeholder={language === 'ar' ? 'مثال: عرض التفاصيل' : 'Button text'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="url" className="wa-input" value={createForm.button_url} onChange={(e) => setCreateForm({ ...createForm, button_url: e.target.value })} placeholder="https://example.com/..." style={{ width: '100%', padding: '8px 12px' }} />
                </>
              )}
              {createForm.button_type === 'phone' && (
                <>
                  <input type="text" className="wa-input" value={createForm.button_phone_text} onChange={(e) => setCreateForm({ ...createForm, button_phone_text: e.target.value })} placeholder={language === 'ar' ? 'مثال: اتصل الآن' : 'Button text (e.g. Call now)'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="tel" className="wa-input" value={createForm.button_phone_number} onChange={(e) => setCreateForm({ ...createForm, button_phone_number: e.target.value })} placeholder="+966501234567" style={{ width: '100%', padding: '8px 12px' }} />
                  <div style={{ fontSize: '11px', color: '#b45309', marginTop: '6px', padding: '6px 10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                    {language === 'ar' ? 'ميتا تتطلب رقم جوال (+966 5XXXXXXXX). أرقام 920 المجانية غير مقبولة.' : 'Meta requires a mobile number (+966 5XXXXXXXX). Toll-free 920 numbers are not accepted.'}
                  </div>
                </>
              )}
              {createForm.button_type === 'quick_reply' && (
                <>
                  <input type="text" className="wa-input" value={createForm.button_quick_1} onChange={(e) => setCreateForm({ ...createForm, button_quick_1: e.target.value })} placeholder={language === 'ar' ? 'مثال: نعم' : 'Option 1 (e.g. Yes)'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="text" className="wa-input" value={createForm.button_quick_2} onChange={(e) => setCreateForm({ ...createForm, button_quick_2: e.target.value })} placeholder={language === 'ar' ? 'مثال: لا' : 'Option 2 (e.g. No)'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="text" className="wa-input" value={createForm.button_quick_3} onChange={(e) => setCreateForm({ ...createForm, button_quick_3: e.target.value })} placeholder={language === 'ar' ? 'مثال: مهتم' : 'Option 3 (e.g. Interested)'} style={{ width: '100%', padding: '8px 12px' }} />
                </>
              )}
              {createForm.button_type === 'url_phone' && (
                <>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'زر 1 (رابط)' : 'Button 1 (URL)'}</div>
                  <input type="text" className="wa-input" value={createForm.button_url_text} onChange={(e) => setCreateForm({ ...createForm, button_url_text: e.target.value })} placeholder={language === 'ar' ? 'مثال: زيارة الموقع' : 'Button text'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="url" className="wa-input" value={createForm.button_url} onChange={(e) => setCreateForm({ ...createForm, button_url: e.target.value })} placeholder="https://example.com/..." style={{ width: '100%', padding: '8px 12px', marginBottom: '10px' }} />
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'زر 2 (اتصال)' : 'Button 2 (Phone)'}</div>
                  <input type="text" className="wa-input" value={createForm.button_secondary_phone_text} onChange={(e) => setCreateForm({ ...createForm, button_secondary_phone_text: e.target.value })} placeholder={language === 'ar' ? 'مثال: اتصل بنا' : 'Button text'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="tel" className="wa-input" value={createForm.button_secondary_phone_number} onChange={(e) => setCreateForm({ ...createForm, button_secondary_phone_number: e.target.value })} placeholder="+966920010213" style={{ width: '100%', padding: '8px 12px' }} />
                </>
              )}
              {createForm.button_type === 'phone_url' && (
                <>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'زر 1 (اتصال)' : 'Button 1 (Phone)'}</div>
                  <input type="text" className="wa-input" value={createForm.button_phone_text} onChange={(e) => setCreateForm({ ...createForm, button_phone_text: e.target.value })} placeholder={language === 'ar' ? 'مثال: اتصل بنا' : 'Button text'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="tel" className="wa-input" value={createForm.button_phone_number} onChange={(e) => setCreateForm({ ...createForm, button_phone_number: e.target.value })} placeholder="+966920010213" style={{ width: '100%', padding: '8px 12px', marginBottom: '10px' }} />
                  <div style={{ fontSize: '11px', color: '#b45309', marginTop: '-4px', marginBottom: '10px', padding: '6px 10px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                    {language === 'ar' ? 'ميتا تتطلب رقم جوال (+966 5XXXXXXXX). أرقام 920 المجانية غير مقبولة.' : 'Meta requires a mobile number (+966 5XXXXXXXX). Toll-free 920 numbers are not accepted.'}
            </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{language === 'ar' ? 'زر 2 (رابط)' : 'Button 2 (URL)'}</div>
                  <input type="text" className="wa-input" value={createForm.button_secondary_url_text} onChange={(e) => setCreateForm({ ...createForm, button_secondary_url_text: e.target.value })} placeholder={language === 'ar' ? 'مثال: زيارة الموقع' : 'Button text'} style={{ width: '100%', padding: '8px 12px', marginBottom: '6px' }} />
                  <input type="url" className="wa-input" value={createForm.button_secondary_url} onChange={(e) => setCreateForm({ ...createForm, button_secondary_url: e.target.value })} placeholder="https://example.com/..." style={{ width: '100%', padding: '8px 12px' }} />
                </>
              )}
            </div>
            </>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '20px' }}>
              {editingDraftId ? (
                <>
                  <button type="button" className="wa-btn wa-btn-primary" onClick={handleSubmitDraft} disabled={submittingDraft} onMouseEnter={(e) => { if (!submittingDraft) e.currentTarget.style.boxShadow = '0 4px 14px rgba(249,115,22,0.35)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }} style={{ padding: '10px 20px', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaPaperPlane />
                    {submittingDraft ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') : (language === 'ar' ? 'إرسال لميتا' : 'Submit to Meta')}
                </button>
                  <button type="button" className="wa-btn wa-btn-secondary" onClick={handleSaveDraft} disabled={savingDraft} onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '10px 20px', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSave />
                    {savingDraft ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'تحديث المسودة' : 'Update draft')}
                  </button>
                </>
              ) : (
                <>
                <button type="button" className="wa-btn wa-btn-primary" onClick={handleCreateSubmit} disabled={creating} onMouseEnter={(e) => { if (!creating) e.currentTarget.style.boxShadow = '0 4px 14px rgba(249,115,22,0.35)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }} style={{ padding: '10px 20px', transition: 'all 0.2s ease' }}>
                  {creating ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') : (language === 'ar' ? 'إرسال للمراجعة' : 'Submit for review')}
                </button>
                  <button type="button" className="wa-btn wa-btn-secondary" onClick={handleSaveDraft} disabled={savingDraft} onMouseEnter={(e) => { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.color = '#b45309'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '10px 20px', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSave />
                    {savingDraft ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (language === 'ar' ? 'حفظ كمسودة' : 'Save as draft')}
                  </button>
                </>
              )}
              <button type="button" className="wa-btn wa-btn-secondary" onClick={() => { setEditingFromTemplate(null); setEditingDraftId(null); setHeaderPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return ''; }); setShowCreateForm(false); setCreateStep(1); }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.color = '#64748b'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '10px 20px', transition: 'all 0.2s ease' }}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="wa-empty-state">
          <FaFileAlt style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '16px' }} />
          <p>{language === 'ar' ? 'لا توجد قوالب' : 'No templates'}</p>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>
            {language === 'ar'
              ? 'أنشئ في ميتا أو انقر «إنشاء قالب» أعلاه'
              : 'Create in Meta or click «Create template» above'}
          </p>
          <a
            href="https://business.facebook.com/wa/manage/message-templates/"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={(e) => { e.currentTarget.style.color = '#ea580c'; e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#f97316'; e.currentTarget.style.textDecoration = 'none'; }}
            style={{ marginTop: '16px', color: '#f97316', fontWeight: '600', fontSize: '14px', transition: 'all 0.2s ease' }}
          >
            {language === 'ar' ? '→ إدارة القوالب في ميتا' : '→ Manage templates in Meta'}
          </a>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {filtered.map((t) => (
            <div
              key={`${t.name}-${t.language || 'ar'}`}
              className="wa-metric-card"
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(249,115,22,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.boxShadow = ''; }}
              style={{
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                {getStatusIcon(t.status)}
                <div style={{ minWidth: 0, textAlign: 'start' }}>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#0f172a', textAlign: 'start' }}>
                    {language === 'ar' ? (t.label_ar || t.name) : (t.label_en || t.name)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'start', direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {t.name} {t.language ? `(${t.language})` : ''}
                  </div>
                  {t.status === 'REJECTED' && t.rejected_reason && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', textAlign: 'start' }}>
                      {language === 'ar' ? 'سبب الرفض: ' : 'Rejected: '}{t.rejected_reason}
                    </div>
                  )}
                  {isTemplatePendingOverTypical(t) && (
                    <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px', textAlign: 'start' }}>
                      {language === 'ar'
                        ? `⚠️ متجاوز متوسط الاعتماد المعتاد (العمر الحالي: ${getAgeLabelFromCreated(t.created_time)})`
                        : `⚠️ Exceeds typical approval average (current age: ${getAgeLabelFromCreated(t.created_time)})`}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button type="button" onClick={() => { setCreateForm(templateToForm(t)); setViewingTemplate(null); setShowCreateForm(true); setCreateStep(1); }} className="wa-btn wa-btn-secondary" onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s ease' }} title={language === 'ar' ? 'استخدم كأساس' : 'Use as base'}>
                  <FaCopy />
                  {language === 'ar' ? 'نسخ' : 'Copy'}
                </button>
                <button type="button" onClick={() => setViewingTemplate(t)} className="wa-btn wa-btn-secondary" onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s ease' }}>
                  <FaEye />
                  {language === 'ar' ? 'عرض' : 'View'}
                </button>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: t.status === 'APPROVED' ? '#dcfce7' : t.status === 'PENDING' ? '#fef3c7' : '#fee2e2',
                    color: t.status === 'APPROVED' ? '#15803d' : t.status === 'PENDING' ? '#b45309' : '#dc2626'
                  }}
                >
                  {getStatusLabel(t.status)}
                </span>
                {isTemplatePendingOverTypical(t) && (
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      fontWeight: '700',
                      backgroundColor: '#fff7ed',
                      color: '#c2410c',
                      border: '1px solid #fdba74'
                    }}
                  >
                    {language === 'ar' ? 'تجاوز المتوسط' : 'Over average'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewingTemplate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            boxSizing: 'border-box',
            overflowY: 'auto'
          }}
          onClick={() => setViewingTemplate(null)}
        >
          <div
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            style={{
              background: '#fff',
              borderRadius: '12px',
              maxWidth: '480px',
              width: '100%',
              maxHeight: 'min(92vh, calc(100vh - 32px))',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              margin: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                flexShrink: 0,
                background: '#fff'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a', textAlign: 'start', flex: 1, minWidth: 0 }}>{language === 'ar' ? (viewingTemplate.label_ar || viewingTemplate.name) : (viewingTemplate.label_en || viewingTemplate.name)}</h3>
              <button type="button" onClick={() => setViewingTemplate(null)} onMouseEnter={(e) => { e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; }} style={{ padding: '6px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '22px', lineHeight: 1, color: '#64748b', transition: 'color 0.2s ease', flexShrink: 0 }} aria-label={language === 'ar' ? 'إغلاق' : 'Close'}>×</button>
            </div>
            <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6, overflowY: 'auto', flex: 1, minHeight: 0, padding: '20px' }}>
              <div><strong>{language === 'ar' ? 'الاسم' : 'Name'}:</strong> {viewingTemplate.name}</div>
              <div style={{ marginTop: '8px' }}><strong>{language === 'ar' ? 'الحالة' : 'Status'}:</strong> {getStatusLabel(viewingTemplate.status)}</div>
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>{language === 'ar' ? 'كما يظهر للعميل' : 'As client will see'}</div>
                {(() => {
                  const carPv = extractCarouselPreviewFromTemplate(viewingTemplate);
                  const bodyText = viewingTemplate.body_text || (viewingTemplate.components || []).find((c) => c.type === 'BODY')?.text || (viewingTemplate.name === 'hello_world' ? 'Hello World' : '');
                  if (carPv?.layout === 'carousel') {
                    return (
                <WhatsAppTemplatePreview
                        bodyText={bodyText}
                        layout="carousel"
                        carouselCards={carPv.carouselCards}
                        carouselQuickReplyText={carPv.carouselQuickReplyText}
                        carouselUrlButtonText={carPv.carouselUrlButtonText}
                        carouselUrl={carPv.carouselUrl}
                        language={language}
                        showSampleSelector={true}
                        phoneFrame={true}
                      />
                    );
                  }
                  return (
                <WhatsAppTemplatePreview
                  bodyText={bodyText}
                  headerFormat={(viewingTemplate.components || []).find((c) => c.type === 'HEADER')?.format || ''}
                  headerText={(viewingTemplate.components || []).find((c) => c.type === 'HEADER')?.text || ''}
                  headerMediaUrl={(viewingTemplate.components || []).find((c) => c.type === 'HEADER')?.example?.header_handle?.[0] || ''}
                  footerText={(viewingTemplate.components || []).find((c) => c.type === 'FOOTER')?.text || ''}
                  buttonType={(() => {
                    const btns = (viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [];
                    const b = btns[0];
                    const cta = btns.filter((x) => x?.type === 'URL' || x?.type === 'PHONE_NUMBER');
                    if (cta.length >= 2) {
                      if (cta[0]?.type === 'URL' && cta[1]?.type === 'PHONE_NUMBER') return 'url_phone';
                      if (cta[0]?.type === 'PHONE_NUMBER' && cta[1]?.type === 'URL') return 'phone_url';
                    }
                    if (b?.type === 'URL') return 'url';
                    if (b?.type === 'PHONE_NUMBER') return 'phone';
                    if (b?.type === 'QUICK_REPLY') return 'quick_reply';
                    return '';
                  })()}
                  buttonUrlText={((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).find((b) => b.type === 'URL')?.text || ''}
                  buttonUrl={((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).find((b) => b.type === 'URL')?.url || ''}
                  buttonPhoneText={((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).find((b) => b.type === 'PHONE_NUMBER')?.text || ''}
                  buttonPhoneNumber={((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).find((b) => b.type === 'PHONE_NUMBER')?.phone_number || ''}
                  buttonSecondaryUrlText={(() => {
                    const cta = ((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
                    return cta[1]?.type === 'URL' ? (cta[1]?.text || '') : '';
                  })()}
                  buttonSecondaryUrl={(() => {
                    const cta = ((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
                    return cta[1]?.type === 'URL' ? (cta[1]?.url || '') : '';
                  })()}
                  buttonSecondaryPhoneText={(() => {
                    const cta = ((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
                    return cta[1]?.type === 'PHONE_NUMBER' ? (cta[1]?.text || '') : '';
                  })()}
                  buttonSecondaryPhoneNumber={(() => {
                    const cta = ((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons || []).filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
                    return cta[1]?.type === 'PHONE_NUMBER' ? (cta[1]?.phone_number || '') : '';
                  })()}
                  buttonQuick={((viewingTemplate.components || []).find((c) => c.type === 'BUTTONS')?.buttons?.filter((b) => b.type === 'QUICK_REPLY').map((b) => b.text) || [])}
                  language={language}
                  showSampleSelector={true}
                />
                  );
                })()}
              </div>
              {(() => {
                const btnComp = (viewingTemplate.components || []).find((c) => c.type === 'BUTTONS');
                const btns = btnComp?.buttons || [];
                if (btns.length === 0) return null;
                return (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#fff7ed', borderRadius: '12px' }}>
                    <strong>{language === 'ar' ? 'الأزرار' : 'Buttons'}:</strong>
                    {btns.map((b, i) => (
                      <div key={i} style={{ marginTop: '6px', fontSize: '12px' }}>
                        {b.type === 'URL' && `🔗 ${b.text} → ${b.url}`}
                        {b.type === 'PHONE_NUMBER' && `📞 ${b.text} (${b.phone_number})`}
                        {b.type === 'QUICK_REPLY' && `💬 ${b.text}`}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {viewingTemplate.status === 'REJECTED' && viewingTemplate.rejected_reason && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#fef2f2', borderRadius: '12px', color: '#b91c1c', fontSize: '12px' }}>
                  <strong>{language === 'ar' ? 'سبب الرفض' : 'Rejection reason'}:</strong> {viewingTemplate.rejected_reason}
                </div>
              )}
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <button type="button" onClick={() => { setEditingFromTemplate({ name: viewingTemplate.name }); setCreateForm(templateToForm(viewingTemplate)); setViewingTemplate(null); setShowCreateForm(true); setCreateStep(3); }} className="wa-btn wa-btn-secondary" onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.color = '#ea580c'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = ''; }} style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease' }}>
                  <FaCopy />
                  {language === 'ar' ? 'استخدم كأساس لقالب جديد' : 'Use as base for new template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplates;
